import { Spin } from "antd";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import { Eye, CreditCard, Hash, User, ChevronRight, Globe } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { motion, AnimatePresence } from "framer-motion";

interface RemittanceTableProps {
  loading: boolean;
  reportData: any[];
  setSelectedRow: (row: any) => void;
  setDrawerOpen: (open: boolean) => void;
}

export const RemittanceTable = ({
  loading,
  reportData,
  setSelectedRow,
  setDrawerOpen,
}: RemittanceTableProps) => {
  return (
    <div className="overflow-auto max-h-[calc(100vh-320px)]">
      <Table className="text-sm min-w-max">
        <TableHeader className="bg-gray-50/50">
          <TableRow className="hover:bg-transparent border-b-gray-100">
            <TableHead className="font-bold text-gray-400 px-8 py-5 uppercase tracking-widest text-[10px]">Reference IDs</TableHead>
            <TableHead className="font-bold text-gray-400 py-5 uppercase tracking-widest text-[10px]">Customer Details</TableHead>
            <TableHead className="font-bold text-gray-400 py-5 text-right uppercase tracking-widest text-[10px]">Settlement Amount</TableHead>
            <TableHead className="font-bold text-gray-400 px-8 py-5 text-center uppercase tracking-widest text-[10px]">Inspection</TableHead>
          </TableRow>
        </TableHeader>

        <TableBody>
          <AnimatePresence mode="popLayout">
            {loading ? (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={4} className="text-center py-32">
                   <div className="flex flex-col items-center gap-4">
                      <Spin size="large" />
                      <p className="text-gray-400 font-medium italic">Processing remittance ledger...</p>
                   </div>
                </TableCell>
              </TableRow>
            ) : reportData.length ? (
              reportData.map((row, i) => (
                <motion.tr 
                  key={`${row.transfer_id}-${i}`}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.02 }}
                  className="group hover:bg-blue-50/30 transition-colors border-b-gray-50 last:border-0"
                >
                  <TableCell className="px-8 py-5">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <Hash className="w-3 h-3 text-blue-400" />
                        <span className="font-black text-xs text-gray-900 tracking-tighter uppercase">{row.transfer_id}</span>
                      </div>
                      <div className="flex items-center gap-2 opacity-50">
                        <CreditCard className="w-3 h-3" />
                        <span className="text-[10px] font-mono">{row.inquiryNumber}</span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 font-bold text-xs border border-indigo-100">
                        {row.name.charAt(0)}
                      </div>
                      <div className="flex flex-col">
                        <span className="font-bold text-gray-800 leading-tight">{row.name}</span>
                        <span className="text-[10px] text-gray-400 font-medium">{row.mobile}</span>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className="font-black text-gray-900 text-lg">
                      {new Intl.NumberFormat("en-LK", {
                        style: "currency",
                        currency: "LKR",
                        minimumFractionDigits: 2,
                      }).format(Number(row.totalamount))}
                    </span>
                  </TableCell>
                  <TableCell className="text-center px-8">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedRow(row);
                        setDrawerOpen(true);
                      }}
                      className="rounded-xl hover:bg-white hover:shadow-md transition-all text-blue-600 font-bold uppercase tracking-widest text-[10px] gap-2"
                    >
                      <Eye className="w-3 h-3" /> Details <ChevronRight className="w-3 h-3" />
                    </Button>
                  </TableCell>
                </motion.tr>
              ))
            ) : (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={4} className="text-center py-32">
                   <div className="flex flex-col items-center gap-4 text-gray-300">
                      <div className="w-20 h-20 rounded-full bg-gray-50 flex items-center justify-center border border-gray-100">
                        <Globe className="w-10 h-10" />
                      </div>
                      <p className="text-lg font-medium italic max-w-xs mx-auto text-center">
                        Execute a search to analyze remittance flows.
                      </p>
                   </div>
                </TableCell>
              </TableRow>
            )}
          </AnimatePresence>
        </TableBody>
      </Table>
    </div>
  );
};
